<template>
  <div
    v-if="isLoading"
    class="loading"
  >
    <h3>{{ trans('core.loading') }}</h3>
    <div class="progress">
      <div
        class="progress-bar progress-bar-striped"
        role="progressbar"
        :style="{ width: loadingProgress * 100 + '%' }"
      />
    </div>
  </div>

  <attendance-matrix-overview
    v-else
    :events="events"
    :students="students"
    :course-title="courseTitle"
    :with-course-exemption="true"
    :only-registered-classes-count="Boolean(onlyRegisteredClassesCount)"
    @showFilter="processShowFilter"
  >
    <attendance-matrix-filter
      slot="filter"
      v-model="filters"
      type="course"
      :model-id="courseId"
      :show-filter="showFilter"
    />
  </attendance-matrix-overview>
</template>

<script>
import AttendanceMatrixFilter from '@/employees/components/Lessons/AttendanceMatrixFilter'
import AttendanceMatrixOverview from '@/employees/components/Lessons/AttendanceMatrixOverview'
import { mapState } from 'vuex'
import { MATRIX_STUDENTS_CHUNK_SIZE, MATRIX_SUBSCRIBABLES_CHUNK_SIZE } from '@/constants'

export default {
  name: 'CourseAttendanceMatrixOverview',

  components: { AttendanceMatrixFilter, AttendanceMatrixOverview },

  props: {
    courseId: {
      required: true,
      type: String,
      default: null,
    },
    courseTitle: {
      required: false,
      type: String,
      default: null,
    },
    onlyRegisteredClassesCount: {
      required: true,
      type: String,
    },
  },

  data () {
    return {
      filters: {
        lecturers: [],
        groups: [],
        courses: [],
        studentGroups: [],
        startdate: null,
        enddate: null,
        attendance: [],
        status: [],
      },
      loadingProgress: 0,
      totalStudentPages: true,
      isLoading: true,
      showFilter: true,
    }
  },

  computed: {
    ...mapState({
      eventIds (state) {
        return Object.values(state.events.events).map(event => event.id)
      },
      studentIds (state) {
        return Object.values(state.students.students).map(student => student.id)
      },
      eventStudents (state) {
        return Object.values(state.events.event_students)
      },

      students (state) {
        const studentEventData = _.groupBy(this.eventStudents, 'student_id')

        return Object.values(state.students.students).filter(student => {
          if (this.filters.studentGroups.length > 0) {
            const studentGoupIds = this.filters.studentGroups.map(group => parseInt(group.id))
            if (!student.groups.some(groupId => studentGoupIds.includes(parseInt(groupId)))) {
              return false
            }
          }

          // Filter by status
          if (this.filters.status.length > 0) {
            if (studentEventData[student.id] == null) {
              return false
            }

            const statusAtEvent = studentEventData[student.id]
              .filter(value => this.events.some(event => event.id == value.event_id))
              .some(value => value.should_attend === true)

            return this.filters.status[0].id === 1 ? statusAtEvent : !statusAtEvent
          }

          return true
        })
      },

      events (state) {
        return Object.values(state.events.events).filter(event => {
          if (this.filters.lecturers.length > 0) {
            const lecturerIds = this.filters.lecturers.map(lecturer => parseInt(lecturer.id))
            if (!event.lecturers.some(lecturerId => lecturerIds.includes(parseInt(lecturerId)))) {
              return false
            }
          }

          if (this.filters.groups.length > 0) {
            const groupIds = this.filters.groups.map(group => parseInt(group.id))
            if (!event.groups.some(groupId => groupIds.includes(parseInt(groupId)))) {
              return false
            }
          }

          if (this.filters.courses.length > 0) {
            const courseIds = this.filters.courses.map(course => parseInt(course.id))
            if (courseIds.includes(parseInt(event.course))) {
              return false
            }
          }

          if (this.filters.startdate != null && moment(event.starttime).isBefore(moment(this.filters.startdate).startOf('day'))) {
            return false
          }

          if (this.filters.enddate != null && moment(event.endtime).isAfter(moment(this.filters.enddate).endOf('day'))) {
            return false
          }

          if (this.filters.attendance.length > 0) {
            return event.attendance_registered == this.filters.attendance[0].id
          }

          return true
        })
      },
    }),
  },

  created () {
    this.loadItems()
  },

  methods: {
    async loadItems () {
      this.isLoading = true
      this.loadingProgress = 0
      const calls = []

      // get all lessons for this course
      await this.$store.dispatch('getCourseMatrixLessons', {
        courseId: this.courseId,
      })

      // get the first page of students this course
      await this.$store.dispatch('getCourseMatrixStudents', {
        courseId: this.courseId,
        queryParams: {
          page: 1,
        },
      }).then((response) => {
        this.totalStudentPages = response.meta.totalPages
      })

      this.loadingProgress = 0.25

      // split the subscribables and students of the grouping in chunks for separate calls
      const chunkedSubscribables = _.chunk(this.eventIds, MATRIX_SUBSCRIBABLES_CHUNK_SIZE)
      const singleStepProgression = 0.75 / (chunkedSubscribables.length + (this.totalStudentPages - 1))

      // Do a call for each chunk and increment the loading progress
      for (const chunk of chunkedSubscribables) {
        calls.push(this.$store.dispatch('getCourseMatrixLessonDetails', {
          courseId: this.courseId,
          queryParams: {
            filter: {
              id: chunk,
            },
          },
        }).then(() => {
          this.loadingProgress += singleStepProgression
        }))
      }

      for (let i = 2; i <= this.totalStudentPages; i++) {
        calls.push(this.$store.dispatch('getCourseMatrixStudents', {
          courseId: this.courseId,
          queryParams: {
            page: i,
          },
        }).then(() => {
          this.loadingProgress += singleStepProgression
        }))
      }

      // wait untill all calls are done so we can group the results
      await Promise.all(calls)

      this.isLoading = false
    },
    processShowFilter (event) {
      this.showFilter = event.showFilter
    },
  },
}
</script>

<style lang="scss" scoped>
.filter-component {
  padding: 0px;

  .control-label {
    padding: 0px;
  }
}

.filter-component-date {
  .control-label {
    padding-top: 10px;
  }
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex: 1 0 auto;

  .progress {
    width: 75%;
  }
}
</style>
